import { useEffect, useState, useRef } from '@wordpress/element'
import {
  revolutSettings,
  PAYMENT_METHODS,
  select,
  onPaymentSuccessHandler,
  createRevolutOrder,
} from '../../utils'
import { useHidePlacerOrderButton } from '../../hooks'

export const RevolutPay = ({
  billing,
  components,
  eventRegistration,
  onSubmit,
  emitResponse,
}) => {
  const { onCheckoutSuccess, onCheckoutFail } = eventRegistration
  const { VALIDATION_STORE_KEY } = window.wc.wcBlocksData
  const settings = revolutSettings(PAYMENT_METHODS.REVOLUT_PAY)
  const [isLoading, setLoading] = useState(false)
  const [isPaymentCanceled, setPaymentCanceled] = useState(false)
  const revolutOrderPublicToken = useRef(null)
  const woocommerceCheckoutSubmissionResult = useRef(null)
  const setPaymentResult = useRef(null)
  const revolutPayRef = useRef(null)
  const destroyRef = useRef()

  const paymentError = errorMsg => ({
    type: emitResponse.responseTypes.ERROR,
    message: errorMsg,
    retry: true,
    messageContext: emitResponse.noticeContexts.PAYMENTS,
  })

  useEffect(() => {
    const initRevolutPayWidget = async () => {
      const { revolutPay, destroy } = await RevolutCheckout.payments({
        publicToken: settings.merchant_public_key,
        locale: settings.locale,
      })

      destroyRef.current = destroy

      if (revolutPayRef.current) {
        revolutPay.mount(revolutPayRef.current, {
          currency: settings.order_currency,
          totalAmount: billing.cartTotal.value,
          mobileRedirectUrls: {
            success: settings.mobile_redirect_url,
            failure: settings.mobile_redirect_url,
            cancel: settings.mobile_redirect_url,
          },
          validate: () => {
            setPaymentCanceled(false)

            return new Promise(resolve => {
              setLoading(true)
              if (select(VALIDATION_STORE_KEY).hasValidationErrors()) {
                setLoading(false)
                resolve(false)
              }

              createRevolutOrder(settings).then(json => {
                revolutOrderPublicToken.current = json.revolut_order_public_id

                onSubmit()

                document.addEventListener('checkout_success', () => {
                  resolve(true)
                })

                document.addEventListener('checkout_fail', () => {
                  setLoading(false)
                  resolve(false)
                })
              })
            })
          },
          createOrder: () => {
            return { publicId: revolutOrderPublicToken.current }
          },
        })
      }

      revolutPay.on('payment', event => {
        switch (event.type) {
          case 'cancel': {
            setPaymentCanceled(true)
            if (setPaymentResult.current) {
              setPaymentResult.current.resolve(paymentError('Payment cancelled!'))
            }
            break
          }

          case 'success': {
            setLoading(true)
            onPaymentSuccessHandler({
              response: woocommerceCheckoutSubmissionResult.current,
              paymentMethod: PAYMENT_METHODS.REVOLUT_PAY,
              shouldSavePayment: 0,
            })
            break
          }

          case 'error': {
            setLoading(false)
            if (setPaymentResult.current) {
              setPaymentResult.current.resolve(paymentError(event.error.message))
            }
            break
          }
          default:
            break
        }
      })
    }

    initRevolutPayWidget()

    return () => {
      destroyRef.current()
    }
  }, [billing.cartTotal.value])

  useEffect(() => {
    const unsubscribeOnCheckoutSuccess = onCheckoutSuccess(async response => {
      return new Promise(resolve => {
        if (isPaymentCanceled) {
          setLoading(false)
          return resolve(paymentError('Payment cancelled!'))
        }
        setLoading(false)
        woocommerceCheckoutSubmissionResult.current = response
        document.dispatchEvent(new CustomEvent('checkout_success'))
        setPaymentResult.current = { resolve }
      })
    })

    const unsubscribeOnCheckoutFail = onCheckoutFail(response => {
      if (!response || !response.paymentDetails || !response.paymentDetails.wc_order_id) {
        document.dispatchEvent(new CustomEvent('checkout_fail'))
      }
    })

    return () => {
      unsubscribeOnCheckoutSuccess()
      unsubscribeOnCheckoutFail()
    }
  }, [onCheckoutSuccess, onCheckoutFail, isPaymentCanceled])

  useHidePlacerOrderButton()

  return (
    <>
      {' '}
      <components.LoadingMask showSpinner={true} isLoading={isLoading}>
        <div ref={revolutPayRef} />
      </components.LoadingMask>
    </>
  )
}
